import classNames from 'classnames/bind';
import { Button, Flex, IconEndLine, Modal, Text, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAppDispatch } from '~/utils/redux/hooks';
import { deleteTuitionById } from '~/thunks/tuition/tuitionThunk';
import { generateGuid } from '~/utils/helper/common';
import { TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { toastActions } from '~/thunks/toast/toastSlice';
import { IToast } from '~/utils/interface/toast';
import styles from './ModalDeleteTuition.module.scss';

type Props = {
  isModalOpen: boolean;
  tuitionId: string | null;
  onClose: () => void;
  onDeleteSuccess: () => void;
};

const cx = classNames.bind(styles);

const ModalDeleteTuition = (props: Props) => {
  //#region Destructuring Props
  const { isModalOpen, tuitionId, onClose, onDeleteSuccess } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Declare State
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  //#endregion Declare State

  //#region Handle Function
  const handleCloseModal = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!tuitionId) return;

    try {
      setIsDeleting(true);
      await dispatch(deleteTuitionById(tuitionId)).unwrap();
      
      // Show success toast
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('common_delete_success_message', { ns: 'common/base' }),
        type: 'success',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);

      handleCloseModal();
      onDeleteSuccess();
    } catch (error) {
      // Show error toast
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('common_delete_error_message', { ns: 'common/base' }),
        type: 'error',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);
    } finally {
      setIsDeleting(false);
    }
  };
  //#endregion Handle Function

  return (
    <Modal open={isModalOpen} label='' size='small'>
      <Modal.Header frameBorder={'none'}>
        <View className={cx('buttonClose')}>
          <View as={'div'} onClick={handleCloseModal} className={cx('iconCloseModal')}>
            <IconEndLine />
          </View>
        </View>
      </Modal.Header>
      <Modal.Body>
      <View as='div' padding='medium'>
          <Text>
            {t('delete_warning_message', { ns: 'common/base' })
              .split('\n')
              .map((line, index) => (
                <span key={index}>
                  {line}
                  {index < t('delete_warning_message', { ns: 'common/base' }).split('\n').length - 1 && <br />}
                </span>
              ))}
          </Text>
        </View>
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyItems='end' gap='xx-small'>
          <Button color='primary-inverse' onClick={handleCloseModal} disabled={isDeleting}>
            {t('cancel_button_label', { ns: 'common/base' })}
          </Button>
          <Button color='danger' onClick={handleDelete} disabled={isDeleting}>
            {isDeleting
              ? t('deleting_button_label', { ns: 'common/base' })
              : t('delete_button_label', { ns: 'common/base' })}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalDeleteTuition;